import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { csvToXlsxBlob } from "../utils/xlsx";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRealtime } from "../contexts/RealtimeContext";
import api from "../services/api";
import LoadingButton from "../components/LoadingButton";
import LoadingSkeleton from "../components/LoadingSkeleton";
import ScannerForm from "../components/ScannerForm";
import AlloggiatiModal from "../components/AlloggiatiModal";
import SesModal from "../components/SesModal";
import MonthExportModal from "../components/MonthExportModal";
import { invoiceRolesForScanner, isInvoiceRoles } from "../utils/invoiceExport";
import { FiFileText, FiFilter } from "react-icons/fi";
import {
  Edit2,
  ChevronDown,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
  Download,
  FileJson,
  FileSpreadsheet,
  Activity,
  Layers,
  Zap,
  Sparkles,
  Code,
  Mail,
  Send,
  Monitor,
} from "lucide-react";
import QRCode from "react-qr-code";
import { FaWhatsapp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { extractInternationalNumber } from "../utils/phoneUtils";
import { track } from "../utils/track";
import EmptyReadNotice from "../components/EmptyReadNotice";
import DuplicateNotice from "../components/DuplicateNotice";
import OutOfCreditsNotice from "../components/OutOfCreditsNotice";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Resultado de la comprobación de requisitos (escáneres con `compliance_rules`,
 * los que crea el registro al venir de «Rechnung prüfen» / VAT invoice
 * checker). Solo lo que el usuario tiene que hacer algo: veredicto y lo que
 * falta o hay que revisar.
 */
/**
 * Origen de cada documento (24-9-2026): chip de color en el historial y filtro.
 * `free_demo_import` es la prueba de la web importada al registrarse.
 */
const SOURCE_META = {
  web: { icon: Monitor, cls: "bg-gray-50 text-gray-700 border-gray-200", key: "scannerDetail.source_web", fb: "Web" },
  whatsapp: { icon: FaWhatsapp, cls: "bg-green-50 text-green-700 border-green-200", key: "scannerDetail.source_whatsapp", fb: "WhatsApp" },
  email: { icon: Mail, cls: "bg-sky-50 text-sky-700 border-sky-200", key: "scannerDetail.source_email", fb: "Email" },
  telegram: { icon: Send, cls: "bg-blue-50 text-blue-700 border-blue-200", key: "scannerDetail.source_telegram", fb: "Telegram" },
  api: { icon: Code, cls: "bg-violet-50 text-violet-700 border-violet-200", key: "scannerDetail.source_api", fb: "API" },
  trial: { icon: Sparkles, cls: "bg-amber-50 text-amber-800 border-amber-200", key: "scannerDetail.source_trial", fb: "Free trial" },
};
const SOURCE_ORDER = ["web", "whatsapp", "email", "telegram", "api", "trial"];

function sourceKeyOf(entry) {
  const s = String(entry?.sourceType || entry?.source_type || "").toLowerCase();
  if (s === "free_demo_import") return "trial";
  if (SOURCE_META[s]) return s;
  return "web"; // manual_upload o sin origen
}

function SourceChip({ entry, t }) {
  const key = sourceKeyOf(entry);
  const meta = SOURCE_META[key];
  const Icon = meta.icon;
  const detail =
    key === "whatsapp"
      ? entry.sourceNumber
      : key === "email"
        ? entry.emailFrom || entry.email_from
        : key === "telegram"
          ? entry.telegramFrom
          : null;
  return (
    <span
      data-source={key}
      className={`inline-flex max-w-[16rem] items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}
      title={detail || ""}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{t(meta.key, meta.fb)}</span>
      {detail && <span className="truncate font-normal opacity-80">· {detail}</span>}
    </span>
  );
}

/**
 * Comprobación de saldo de un extracto (escáneres con `balance_check`): saldo
 * inicial + movimientos = saldo final. Si no cuadra, la primera fila donde el
 * saldo impreso deja de cuadrar.
 */
function BalanceSummary({ bc, t }) {
  if (!bc) return null;
  const style =
    bc.ok === true
      ? "border-green-200 bg-green-50 text-green-900"
      : bc.ok === false
        ? "border-red-200 bg-red-50 text-red-900"
        : "border-amber-200 bg-amber-50 text-amber-900";
  const key = bc.ok === true ? "ok" : bc.ok === false ? "fail" : "incomplete";
  return (
    <div className={`mb-4 rounded-xl border p-3 text-sm ${style}`}>
      <span className="font-semibold">
        {t(`scannerDetail.balance.${key}`, { rows: bc.rows, diff: Math.abs(bc.difference || 0).toFixed(2) })}
      </span>
      {bc.ok === false && bc.first_mismatch_row ? (
        <span className="ml-2">{t("scannerDetail.balance.row", { row: bc.first_mismatch_row })}</span>
      ) : null}
    </div>
  );
}

function ComplianceSummary({ compliance, t }) {
  if (!compliance) return null;
  if (compliance.error || !Array.isArray(compliance.checks)) {
    return (
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        {t("scannerDetail.compliance.error")}
      </div>
    );
  }
  const style = {
    pass: "border-green-200 bg-green-50 text-green-900",
    fail: "border-red-200 bg-red-50 text-red-900",
    review: "border-amber-200 bg-amber-50 text-amber-900",
  }[compliance.verdict] || "border-amber-200 bg-amber-50 text-amber-900";
  const open = compliance.checks.filter((c) => c.status === "missing" || c.status === "unsure");
  return (
    <div className={`mb-4 rounded-xl border p-3 ${style}`}>
      <div className="text-sm font-semibold">
        {t(`scannerDetail.compliance.${compliance.verdict}`)}
        <span className="ml-2 text-xs font-normal opacity-70">
          {t(`scannerDetail.compliance.rules_${compliance.rules}`)}
          {compliance.mode === "small" ? ` · ${t("scannerDetail.compliance.small")}` : ""}
        </span>
      </div>
      {open.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm">
          {open.map((c) => (
            <li key={c.key} className="flex items-start gap-2">
              <span className={c.status === "missing" ? "text-red-600" : "text-amber-600"}>
                {c.status === "missing" ? "✗" : "?"}
              </span>
              <span>
                {t(`scannerDetail.compliance.checks.${c.key}`)}{" "}
                <span className="text-xs opacity-70">
                  ({t(`scannerDetail.compliance.status_${c.status}`)})
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HistoryItem({
  entry,
  t,
  onDownloadResults,
  onCleanJsonForUser,
  selectable,
  selected,
  onToggleSelect,
  autoExpand,
  highlight,
}) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (autoExpand) setExpanded(true);
  }, [autoExpand]);

  useEffect(() => {
    if (!highlight) return;
    const node = containerRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);
  const rawStatus = (entry && entry.status) || "done";
  const statusKey = rawStatus === "done" ? "completed" : rawStatus;
  const statusLabel =
    t(`scannerDetail.status_${rawStatus === "done" ? "done" : statusKey}`) ||
    t(`scannerDetail.status_${statusKey}`) ||
    statusKey;

  return (
    <motion.div
      ref={containerRef}
      data-track-area="result"
      data-track-private=""
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`border-b border-gray-100 last:border-0 group ${
        highlight ? "bg-primary-50/40" : ""
      }`}
    >
      <div
        onClick={() => {
          // Abrir un resultado = mirarlo; con `copy`/`export` dice si se lo llevaron y cómo (26-10-2026).
          if (!expanded) track("result_expand", { source: sourceKeyOf(entry), status: rawStatus });
          setExpanded(!expanded);
        }}
        className={`flex items-center justify-between p-4 cursor-pointer transition-all duration-200 ${
          expanded ? "bg-primary-50/50" : "hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-4">
          {selectable && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect && onToggleSelect(entry);
              }}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                selected
                  ? "bg-primary-600 border-primary-600 text-white"
                  : "border-gray-300 hover:border-primary-400"
              }`}
              title={t("scannerDetail.select_result")}
            >
              {selected && (
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          )}
          <div
            className={`p-2.5 rounded-xl transition-all duration-300 ${
              statusKey === "completed"
                ? "bg-green-100 text-green-600 group-hover:bg-green-200"
                : statusKey === "processing"
                ? "bg-blue-100 text-blue-600 animate-pulse"
                : statusKey === "queued"
                ? "bg-yellow-100 text-yellow-600"
                : "bg-red-100 text-red-600"
            }`}
          >
            {statusKey === "completed" ? (
              <CheckCircle className="w-5 h-5" />
            ) : statusKey === "processing" || statusKey === "queued" ? (
              <Clock className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="font-semibold text-gray-900">
                {entry.splitPart?.parent_filename ||
                  entry.filename ||
                  entry.fileName ||
                  t("scannerDetail.default_filename")}
              </div>
              {entry.splitPart && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-violet-50 text-violet-700 border-violet-200 whitespace-nowrap">
                  {t("scannerDetail.split_part", {
                    index: entry.splitPart.index,
                    total: entry.splitPart.total,
                  })}
                </span>
              )}
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full capitalize text-[10px] font-bold tracking-wide border ${
                    statusKey === "completed"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : statusKey === "processing"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : statusKey === "queued"
                      ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                      : "bg-red-50 text-red-700 border-red-200"
                  }`}
                >
                  {statusLabel}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
              <span>
                {entry.createdAt
                  ? new Date(entry.createdAt).toLocaleString(i18n.language)
                  : ""}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <SourceChip entry={entry} t={t} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-bold text-gray-900">
              {t("scannerDetail.fields_extracted_count", {
                count: entry.summary?.fields_with_values || 0,
              })}
            </div>
            <div className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-md inline-block mt-1">
              {t("scannerDetail.tokens_used", {
                count: entry.tokensUsed || 0,
              })}
            </div>
          </div>
          <div
            className={`transition-transform duration-300 ${
              expanded ? "rotate-180" : ""
            }`}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-gray-50/50 border-t border-gray-100"
          >
            <div className="p-4">
              <EmptyReadNotice extractedJson={entry.extractedJson} t={t} />
              <DuplicateNotice extractedJson={entry.extractedJson} t={t} />
              <ComplianceSummary compliance={entry.extractedJson?.compliance} t={t} />
              <BalanceSummary bc={entry.extractedJson?.balance_check} t={t} />
              {entry.extractedJson?.arithmetic?.ok === false && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {t("scannerDetail.arithmetic_fail")}
                </div>
              )}
              {entry.fields && entry.fields.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {entry.fields.map((field, idx) => (
                    <div
                      key={idx}
                      data-track-field={field.name || field.label}
                      className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                        <span>{field.label || field.name}</span>
                        {field.confidence && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              field.confidence > 0.8
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {Math.round(field.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-900 font-medium break-words font-mono bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100">
                        {field.value !== null && field.value !== undefined ? (
                          String(field.value)
                        ) : (
                          <span className="text-gray-400 italic">Null</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">
                  {t("scannerDetail.no_fields_extracted")}
                </div>
              )}

              {entry.extractedJson && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-xs font-medium text-gray-600">
                    <span className="font-bold text-gray-800 text-sm block mb-1">
                      {t("scannerDetail.export_data_title")}
                    </span>
                    {t("scannerDetail.export_data_description")}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-track="export_json"
                      onClick={() => onDownloadResults(entry, "json")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white hover:bg-black rounded-lg text-xs font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <FileJson className="w-4 h-4" />
                      JSON
                    </button>
                    <button
                      data-track="export_csv"
                      onClick={() => onDownloadResults(entry, "csv")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-xs font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      CSV
                    </button>
                    <button
                      data-track="export_excel"
                      onClick={() => onDownloadResults(entry, "excel")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-xs font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function ScannerDetail() {
  // Use the default translation namespace and reference scannerDetail keys
  // explicitly (e.g. t('scannerDetail.process_document')).
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [scanner, setScanner] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [files, setFiles] = useState([]);
  // Aviso tras enviar: sin él, el botón volvía a quedar activo con el mismo
  // archivo y se reenviaba (una usuaria pagó 3 páginas dos veces, 25-9-2026).
  const [sentCount, setSentCount] = useState(0);
  // Sin créditos: en vez de un error seco, el camino a los planes. Es el
  // momento en que un usuario activo está más dispuesto a pagar.
  const [outOfCredits, setOutOfCredits] = useState(false);
  const isFreePlan = String(user?.subscription_plan || user?.subscription?.plan || "free").toLowerCase() === "free";
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [tokens, setTokens] = useState(0);
  const [sourceFilter, setSourceFilter] = useState("all");
  // Recuento por origen de todo el historial (lo da el servidor en la 1.ª página).
  const [sourceCounts, setSourceCounts] = useState(null);
  const sourceFilterRef = useRef("all");
  const [userPhones, setUserPhones] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showAlloggiati, setShowAlloggiati] = useState(false);
  const [showSes, setShowSes] = useState(false);
  const [showMonthExport, setShowMonthExport] = useState(false);
  // Plantilla de facturas (regla en utils/invoiceExport.js): «Descargar el mes».
  const invoiceRoles = invoiceRolesForScanner(scanner);
  const isInvoiceScanner = isInvoiceRoles(invoiceRoles, scanner);
  // Plantilla de DNI/pasaportes con los campos del registro de huéspedes.
  const isGuestIdScanner = Boolean(
    scanner?.fields?.some((f) => f?.name === "surname") &&
      scanner?.fields?.some((f) => f?.name === "nationality_code")
  );
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [bulkFormat, setBulkFormat] = useState("json");
  const saasNumber = process.env.REACT_APP_SAAS_NUMBER;
  const whatsappLink = process.env.REACT_APP_WHATSAPP_LINK;
  const { lastEvent, jobs, registerResultOrigin } = useRealtime();
  const lastProcessedEventRef = useRef(null);
  const [focusedResultId, setFocusedResultId] = useState(null);

  useEffect(() => {
    if (!location.state?.focusResultId) return;
    setFocusedResultId(location.state.focusResultId);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  const normalizeTokens = (data) =>
    typeof data === "number"
      ? data
      : Number(data?.tokens_balance ?? data?.tokens ?? 0);

  const fetchHistory = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (!user) return;
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        // El filtro por origen va al servidor: así aparecen también los
        // documentos antiguos de ese origen, no solo los ya cargados.
        const source = sourceFilterRef.current;
        const params = {
          ...(cursor ? { cursor } : {}),
          ...(SOURCE_META[source] ? { source } : {}),
        };
        const response = await api.getScannerHistory(id, params);
        const items = response?.items || [];
        if (response?.sourceCounts) setSourceCounts(response.sourceCounts);
        setHistory((prev) => {
          const newItems = append ? [...prev, ...items] : items;
          if (typeof response?.sourceCounts?.all === "number") {
            setHistoryCount(response.sourceCounts.all);
          } else if (!SOURCE_META[source] && !response?.sourceCounts) {
            setHistoryCount(
              typeof response?.count === "number" && !append
                ? response.count
                : newItems.length
            );
          }
          return newItems;
        });
        setHistoryCursor(response?.nextCursor || null);
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Unable to load history.";
        setHistoryError(message);
      } finally {
        setHistoryLoading(false);
      }
    },
    [id, user]
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [scannerResponse, tokensResponse] = await Promise.all([
          api.getScanner(id),
          api.getTokens(),
        ]);
        if (!cancelled) {
          setScanner(scannerResponse);
          setTokens(normalizeTokens(tokensResponse));
        }
        // Get user phones for filter dropdown
        try {
          const userData = await api.getUser();
          const rawPhones = userData?.phones || [];
          let parsedPhonesRaw = rawPhones;
          if (typeof rawPhones === "string") {
            try {
              parsedPhonesRaw = JSON.parse(rawPhones);
            } catch (err) {
              const matched = (rawPhones.match(/\+\d{6,15}/g) || []).map(
                (m) => m
              );
              parsedPhonesRaw = matched.length ? matched : [rawPhones];
            }
          }
          const parsedPhones = (
            Array.isArray(parsedPhonesRaw) ? parsedPhonesRaw : [parsedPhonesRaw]
          )
            .map((p) => {
              const extracted = extractInternationalNumber(p);
              return extracted || null;
            })
            .filter(Boolean);
          if (!cancelled) {
            setUserPhones(parsedPhones);
          }
        } catch (err) {
          console.warn("Could not fetch user phones", err);
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Unable to load scanner details"
        );
      } finally {
        if (!cancelled) {
          fetchHistory();
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [user, id, fetchHistory]);

  // Auto-refresh history when realtime events indicate a completed/error result
  useEffect(() => {
    if (!lastEvent) return;
    // Only process completed or error events
    if (lastEvent.eventType !== "completed" && lastEvent.eventType !== "error")
      return;
    // Avoid processing the same event twice
    const eventKey = `${lastEvent.resultId}-${lastEvent.eventType}-${lastEvent.timestamp}`;
    if (lastProcessedEventRef.current === eventKey) return;
    lastProcessedEventRef.current = eventKey;

    console.log("[ScannerDetail] Realtime event received, refreshing history", {
      eventType: lastEvent.eventType,
      resultId: lastEvent.resultId,
    });

    // Refresh history and tokens
    fetchHistory();
    api
      .getTokens()
      .then((data) => setTokens(normalizeTokens(data)))
      .catch(() => {});
  }, [lastEvent, fetchHistory]);

  const handleUpdate = async (payload) => {
    setUpdating(true);
    try {
      const response = await api.updateScanner(id, payload);
      // The API might return the updated item directly or inside a wrapper
      const updatedItem = response.item || response;
      setScanner(updatedItem);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to update scanner:", err);
      setError(t("scanners.update_error") || "Failed to update scanner");
    } finally {
      setUpdating(false);
    }
  };

  const submit = async () => {
    if (files.length === 0) {
      setError(t("scannerDetail.select_file_error"));
      return;
    }

    setProcessing(true);
    setError(null);
    setResults(null);
    setSentCount(0);
    setOutOfCredits(false);
    let sent = 0;

    try {
      // Process each file sequentially
      for (const file of files) {
        const response = await api.processUpload(id, file);

        if (response.success) {
          if (response.resultId) {
            registerResultOrigin?.({
              resultId: response.resultId,
              scannerId: id,
              scannerName: scanner?.name || null,
            });
          }
          // For multiple files, we don't set results, just update history and tokens
          if (response.status === "queued") {
            // update history so user sees queued item
            fetchHistory();
          }

          // Actualizar tokens
          const data = await api.getTokens();
          setTokens(normalizeTokens(data));

          // Actualizar historial
          fetchHistory();
          sent += 1;
        } else {
          setError(response.message || t("scannerDetail.processing_failed"));
          break; // Stop on first error
        }
      }
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 402 && data.next_action?.type !== "billing_portal") {
        setOutOfCredits({ required: data.tokensRequired, available: data.tokensAvailable });
        return;
      }
      const errorMessage =
        data.message ||
        err.message ||
        t("scannerDetail.error_processing");
      setError(errorMessage);
    } finally {
      setProcessing(false);
      if (sent > 0) {
        setSentCount(sent);
        setFiles([]);
      }
    }
  };

  const downloadResults = async (format = "json") => {
    track("export", { format, where: "scanner_result" });
    if (!results) return;

    let dataStr, mimeType, extension;

    switch (format) {
      case "json":
        const cleanedData = cleanJsonForUser(results);
        dataStr = JSON.stringify(cleanedData, null, 2);
        mimeType = "application/json";
        extension = "json";
        break;

      case "csv":
        dataStr = asRows(results.extractedFields)
          ? linesToRows([{ fields: results.extractedFields, filename: results.filename }])
          : convertToCSV(results.extractedFields);
        mimeType = "text/csv;charset=utf-8;";
        extension = "csv";
        break;

      case "excel":
        dataStr = asRows(results.extractedFields)
          ? "\uFEFF" + linesToRows([{ fields: results.extractedFields, filename: results.filename }])
          : convertToExcelCSV(results.extractedFields);
        mimeType = XLSX_MIME;
        extension = "xlsx";
        break;

      default:
        return;
    }

    const dataBlob =
      extension === "xlsx" ? await csvToXlsxBlob(dataStr) : new Blob([dataStr], { type: mimeType });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `extraction-${
      results.filename || results.requestId || "results"
    }.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Helper function to normalize field label (use name if label seems to be a description)
  const normalizeFieldLabel = (field) => {
    if (!field.label || !field.name) return field.name || field.label;
    // If label is very long (>50 chars) or contains common description words, use name instead
    const label = String(field.label).trim();
    const isDescription =
      label.length > 50 ||
      label.toLowerCase().includes("the ") ||
      label.toLowerCase().includes("assigned") ||
      label.toLowerCase().includes("billed") ||
      label.toLowerCase().includes("including");
    return isDescription ? field.name : label;
  };

  const cleanJsonForUser = (data) => {
    if (!data) return null;

    // Extraer solo la información relevante para el usuario
    const cleaned = {
      scanner_name: data.scanner_name,
      extracted_at: data.extracted_at,
      filename: data.filename,
      fields: (data.fields || []).map((field) => ({
        name: field.name,
        label: normalizeFieldLabel(field), // Normalize label to ensure consistency
        value: field.value,
        confidence: field.confidence,
        source_fragment: field.source_fragment,
        ...(field.normalization_notes &&
          field.normalization_notes.trim() && {
            normalization_notes: field.normalization_notes,
          }),
      })),
    };

    return cleaned;
  };

  const downloadHistoryResults = async (entry, format = "json") => {
    track("export", { format, where: "history_row" });
    if (!entry) return;

    let dataStr, mimeType, extension;
    const filename =
      entry.filename ||
      entry.fileName ||
      `extraction-${entry.resultId || entry.requestId}`;

    const rawData = entry.extractedJson || entry;

    switch (format) {
      case "json":
        const cleanedData = cleanJsonForUser(rawData);
        dataStr = JSON.stringify(cleanedData, null, 2);
        mimeType = "application/json";
        extension = "json";
        break;

      case "csv":
        const fields =
          entry.fields ||
          (entry.extractedJson && entry.extractedJson.fields) ||
          [];
        dataStr = asRows(fields) ? linesToRows([entry]) : convertToCSV(fields);
        mimeType = "text/csv;charset=utf-8;";
        extension = "csv";
        break;

      case "excel":
        const excelFields =
          entry.fields ||
          (entry.extractedJson && entry.extractedJson.fields) ||
          [];
        dataStr = asRows(excelFields)
          ? "\uFEFF" + linesToRows([entry])
          : convertToExcelCSV(excelFields);
        mimeType = XLSX_MIME;
        extension = "xlsx";
        break;

      default:
        return;
    }

    const dataBlob =
      extension === "xlsx" ? await csvToXlsxBlob(dataStr) : new Blob([dataStr], { type: mimeType });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // --- Bulk selection and download functions ---
  const isEntrySelectable = (entry) => {
    const rawStatus = (entry && entry.status) || "done";
    const statusKey = rawStatus === "done" ? "completed" : rawStatus;
    return statusKey === "completed";
  };

  const toggleSelectEntry = (entry) => {
    setSelectedEntries((prev) => {
      const entryId = entry.resultId || entry.requestId;
      const isSelected = prev.some(
        (e) => (e.resultId || e.requestId) === entryId
      );
      if (isSelected) {
        return prev.filter((e) => (e.resultId || e.requestId) !== entryId);
      } else {
        return [...prev, entry];
      }
    });
  };

  const selectAllSelectable = (filteredHistory) => {
    const selectableEntries = filteredHistory.filter(isEntrySelectable);
    setSelectedEntries(selectableEntries);
  };

  const clearSelection = () => {
    setSelectedEntries([]);
  };

  // Cabecera legible de una columna exportada: la etiqueta del campo (o de la
  // columna de la tabla) en la plantilla o en el resultado; si no hay, el nombre.
  const exportLabel = (entries, name, isItem = false) => {
    const defs = scanner?.fields || [];
    if (isItem) {
      for (const d of defs) {
        const it = (d.item_fields || []).find((x) => x.name === name);
        if (it?.label) return it.label;
      }
      return name;
    }
    const def = defs.find((d) => d.name === name);
    if (def?.label) return def.label;
    for (const e of entries) {
      const f = (e.fields || e.extractedJson?.fields || []).find((x) => x?.name === name);
      if (f?.label) return f.label;
    }
    return name;
  };

  const convertMultipleToCsv = (entries) => {
    if (!entries || entries.length === 0) return "";
    if (entries.some((e) => tableFieldOf(e.fields || e.extractedJson?.fields))) {
      return linesToRows(entries);
    }

    // Gather all unique field names across all entries
    const allFieldNames = new Set();
    entries.forEach((entry) => {
      const fields =
        entry.fields ||
        (entry.extractedJson && entry.extractedJson.fields) ||
        [];
      fields.forEach((f) => {
        if (f.name) allFieldNames.add(f.name);
      });
    });

    const fieldNameArray = Array.from(allFieldNames);
    const headers = [
      t("scannerDetail.export_file"),
      t("scannerDetail.export_date"),
      ...fieldNameArray.map((n) => exportLabel(entries, n)),
    ];

    const rows = entries.map((entry) => {
      const fields =
        entry.fields ||
        (entry.extractedJson && entry.extractedJson.fields) ||
        [];
      const fieldMap = {};
      fields.forEach((f) => {
        if (f.name)
          fieldMap[f.name] =
            f.value !== null && f.value !== undefined ? String(f.value) : "";
      });

      const row = [
        entry.filename || entry.fileName || "Document",
        entry.createdAt ? new Date(entry.createdAt).toLocaleString(i18n.language) : "",
        ...fieldNameArray.map((name) => fieldMap[name] || ""),
      ];
      return row;
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    return csvContent;
  };

  const convertMultipleToExcelCsv = (entries) => {
    if (!entries || entries.length === 0) return "";
    if (entries.some((e) => tableFieldOf(e.fields || e.extractedJson?.fields))) {
      return "\uFEFF" + linesToRows(entries);
    }

    const BOM = "\uFEFF";

    // Gather all unique field names across all entries
    const allFieldNames = new Set();
    entries.forEach((entry) => {
      const fields =
        entry.fields ||
        (entry.extractedJson && entry.extractedJson.fields) ||
        [];
      fields.forEach((f) => {
        if (f.name) allFieldNames.add(f.name);
      });
    });

    const fieldNameArray = Array.from(allFieldNames);
    const headers = [
      t("scannerDetail.export_file"),
      t("scannerDetail.export_date"),
      ...fieldNameArray.map((n) => exportLabel(entries, n)),
    ];

    const rows = entries.map((entry) => {
      const fields =
        entry.fields ||
        (entry.extractedJson && entry.extractedJson.fields) ||
        [];
      const fieldMap = {};
      fields.forEach((f) => {
        if (f.name)
          fieldMap[f.name] =
            f.value !== null && f.value !== undefined ? String(f.value) : "";
      });

      const row = [
        entry.filename || entry.fileName || "Document",
        entry.createdAt ? new Date(entry.createdAt).toLocaleString(i18n.language) : "",
        ...fieldNameArray.map((name) => fieldMap[name] || ""),
      ];
      return row;
    });

    // Use \r\n (CRLF) line endings for Excel compatibility
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")
      )
      .join("\r\n");

    return BOM + csvContent;
  };

  const handleConfirmBulkDownload = async () => {
    if (selectedEntries.length === 0) return;
    track("export", { format: bulkFormat, where: "history_bulk", n: selectedEntries.length });

    let dataStr, mimeType, extension;
    const timestamp = new Date().toISOString().split("T")[0];

    switch (bulkFormat) {
      case "json":
        const cleanedData = selectedEntries.map((entry) => {
          const rawData = entry.extractedJson || entry;
          return cleanJsonForUser(rawData);
        });
        dataStr = JSON.stringify(cleanedData, null, 2);
        mimeType = "application/json";
        extension = "json";
        break;

      case "csv":
        dataStr = convertMultipleToCsv(selectedEntries);
        mimeType = "text/csv;charset=utf-8;";
        extension = "csv";
        break;

      case "excel":
        dataStr = convertMultipleToExcelCsv(selectedEntries);
        mimeType = XLSX_MIME;
        extension = "xlsx";
        break;

      default:
        return;
    }

    const dataBlob =
      extension === "xlsx" ? await csvToXlsxBlob(dataStr) : new Blob([dataStr], { type: mimeType });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bulk-export-${timestamp}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);

    setShowBulkDownloadModal(false);
    setSelectedEntries([]);
  };

  // Documentos con líneas (movimientos de un extracto, líneas de albarán o de
  // pedido): una fila por línea, con los campos de cabecera repetidos en cada
  // una. Antes el campo tabla salía como «[object Object]» en el CSV/Excel.
  const tableFieldOf = (fields) =>
    (fields || []).find(
      (f) => Array.isArray(f.value) && f.value.some((r) => r && typeof r === "object")
    );
  // Formato de filas (una por registro) si hay tabla o si algún campo se
  // repite: varias tarjetas de visita en una misma hoja.
  const asRows = (fields) => {
    if (tableFieldOf(fields)) return true;
    const names = (fields || []).map((f) => f?.name).filter(Boolean);
    return new Set(names).size < names.length;
  };

  const linesToRows = (entries) => {
    const withFields = entries.map((entry) => ({
      entry,
      fields: entry.fields || (entry.extractedJson && entry.extractedJson.fields) || [],
    }));
    const scalarNames = [];
    const itemKeys = [];
    withFields.forEach(({ fields }) => {
      const table = tableFieldOf(fields);
      fields.forEach((f) => {
        if (f !== table && f.name && !scalarNames.includes(f.name)) scalarNames.push(f.name);
      });
      (table?.value || []).forEach((row) =>
        Object.keys(row || {}).forEach((k) => {
          if (!itemKeys.includes(k)) itemKeys.push(k);
        })
      );
    });
    const cell = (v) => (v === null || v === undefined ? "" : String(v));
    // Varios registros en un documento sin tabla (una hoja con 4 tarjetas de
    // visita devuelve full_name ×4, email ×4…): cada vez que un nombre se
    // repite empieza un registro nuevo, y cada registro es una fila. Antes se
    // tomaba solo el primero y el Excel perdía 3 de cada 4 contactos (25-9-2026).
    const recordsOf = (fields, table) => {
      const recs = [];
      let cur = {};
      fields.forEach((f) => {
        if (!f?.name || f === table) return;
        if (Object.prototype.hasOwnProperty.call(cur, f.name)) {
          recs.push(cur);
          cur = {};
        }
        cur[f.name] = f.value;
      });
      recs.push(cur);
      return recs;
    };
    const rows = [];
    withFields.forEach(({ entry, fields }) => {
      const table = tableFieldOf(fields);
      recordsOf(fields, table).forEach((rec) => {
        const head = [
          entry.filename || entry.fileName || "Document",
          entry.createdAt ? new Date(entry.createdAt).toLocaleString(i18n.language) : "",
          ...scalarNames.map((n) => cell(rec[n])),
        ];
        const lines = table?.value?.length ? table.value : [{}];
        lines.forEach((line) => rows.push([...head, ...itemKeys.map((k) => cell(line?.[k]))]));
      });
    });
    return [
      [
        t("scannerDetail.export_file"),
        t("scannerDetail.export_date"),
        ...scalarNames.map((n) => exportLabel(entries, n)),
        ...itemKeys.map((k) => exportLabel(entries, k, true)),
      ],
      ...rows,
    ]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
  };

  const convertToCSV = (fields) => {
    if (!fields || fields.length === 0) return "";

    // Headers
    const headers = [
      t("scannerDetail.export_field"),
      t("scannerDetail.export_value"),
      t("scannerDetail.export_confidence"),
    ];

    // Rows
    const rows = fields.map((field) => [
      field.label || field.name || "",
      field.value !== null && field.value !== undefined ? String(field.value) : "",
      field.confidence ? Math.round(field.confidence * 100) : "",
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    return csvContent;
  };

  const convertToExcelCSV = (fields) => {
    if (!fields || fields.length === 0) return "";

    // Excel-compatible CSV with BOM for proper encoding
    const BOM = "\uFEFF";

    // Headers
    const headers = [
      t("scannerDetail.export_field"),
      t("scannerDetail.export_value"),
      t("scannerDetail.export_confidence"),
    ];

    // Rows
    const rows = fields.map((field) => [
      field.label || field.name || "",
      field.value !== null && field.value !== undefined ? String(field.value) : "",
      field.confidence ? Math.round(field.confidence * 100) : "",
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    return BOM + csvContent;
  };

  const resetForm = () => {
    setFiles([]);
    setResults(null);
    setError(null);
  };

  return (
    <>
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6 max-w-7xl mx-auto space-y-8"
      >
        {scanner ? (
          <>
            {/* Top Section: Scanner Info & Edit */}
            <motion.div
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-xl border border-white/60 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-32 bg-gradient-to-br from-primary-100/30 to-purple-100/30 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

              {isEditing ? (
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-xl bg-primary-100 text-primary-600 shadow-sm">
                      <Edit2 className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {t("scanners.edit_scanner_section") || "Edit Scanner"}
                    </h2>
                  </div>
                  <ScannerForm
                    initial={scanner}
                    onSave={handleUpdate}
                    creating={updating}
                    isEditing={true}
                    onCancel={() => setIsEditing(false)}
                  />
                </div>
              ) : (
                <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <button
                        onClick={() => navigate("/scanners")}
                        className="group p-2 bg-white text-gray-400 hover:text-primary-600 hover:bg-white hover:shadow-md rounded-xl transition-all duration-200 border border-transparent hover:border-gray-100"
                        title={t("scannerDetail.back_to_scanners", "Back to templates")}
                      >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
                      </button>
                      <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                        {scanner.name}
                      </h1>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                        title={
                          t("scanners.edit_scanner_title") || "Edit Scanner"
                        }
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-gray-500 text-lg mb-6 leading-relaxed max-w-2xl">
                      {scanner.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {scanner.fields &&
                        scanner.fields.map((f, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-gray-100/80 text-gray-600 border border-gray-200"
                          >
                            {normalizeFieldLabel(f)}
                          </span>
                        ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="bg-white/60 p-4 rounded-2xl border border-white/50 shadow-sm min-w-[140px]">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                          Tokens
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 ml-1">
                        {tokens}
                      </div>
                    </div>

                    <div className="bg-white/60 p-4 rounded-2xl border border-white/50 shadow-sm min-w-[140px]">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                          <Layers className="w-4 h-4" />
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                          {t("scanners.uploads_count", "Scans")}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 ml-1">
                        {historyCount}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Action Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Manual Upload */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="lg:col-span-7"
              >
                <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 h-full flex flex-col">
                  <div className="space-y-6 flex-1">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {t("scannerDetail.upload_documents")}
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        {t("scannerDetail.select_file_for", {
                          scannerName: scanner.name,
                        })}
                      </p>

                      <div className="relative group">
                        <input
                          type="file"
                          id="file-upload"
                          accept=".pdf,.png,.jpg,.jpeg,.gif"
                          multiple
                          onChange={(e) => {
                            setFiles(Array.from(e.target.files));
                            setSentCount(0);
                          }}
                          disabled={processing}
                          className="hidden"
                        />
                        <label
                          htmlFor="file-upload"
                          className={`transition-all duration-300 flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-2xl cursor-pointer overflow-hidden relative ${
                            files.length > 0
                              ? "border-primary-500 bg-primary-50/50"
                              : "border-gray-200 hover:border-primary-400 hover:bg-gray-50"
                          }`}
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-primary-50/0 group-hover:from-primary-50/30 group-hover:to-transparent transition-all pointer-events-none" />
                          {files.length > 0 ? (
                            <div className="relative z-10 text-center">
                              <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-3 text-primary-600">
                                <FiFileText className="w-8 h-8" />
                              </div>
                              <span className="text-lg font-bold text-gray-900 block">
                                {files.length === 1
                                  ? files[0].name
                                  : `${files.length} files selected`}
                              </span>
                              {files.length === 1 && (
                                <span className="text-sm text-primary-600 mt-1 font-medium bg-primary-50 px-2 py-1 rounded-lg inline-block">
                                  {(files[0].size / 1024 / 1024).toFixed(2)} MB
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="relative z-10 text-center">
                              <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:bg-primary-50 group-hover:text-primary-600 transition-all duration-300">
                                <Download className="w-7 h-7 text-gray-400 group-hover:text-primary-600 transition-colors" />
                              </div>
                              <span className="text-base font-semibold text-gray-900 block mb-1">
                                {t("scannerDetail.click_to_upload")}
                              </span>
                              <span className="text-xs text-gray-400">
                                {t("scannerDetail.supported_file_types")}
                              </span>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    {outOfCredits && (
                      <OutOfCreditsNotice
                        required={outOfCredits.required}
                        available={outOfCredits.available}
                        isFreePlan={isFreePlan}
                        where="scanner_detail"
                      />
                    )}

                    {sentCount > 0 && !error && (
                      <div
                        data-testid="upload-sent"
                        className="p-4 bg-green-50 text-green-800 text-sm rounded-xl border border-green-100 flex items-start gap-3"
                      >
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>{t("scannerDetail.upload_sent", { count: sentCount })}</span>
                      </div>
                    )}

                    {error && (
                      <div className="p-4 bg-red-50 text-red-700 text-sm rounded-xl border border-red-100 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <LoadingButton
                      onClick={submit}
                      loading={processing}
                      disabled={files.length === 0}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 rounded-xl font-bold shadow-lg shadow-primary-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none hover:translate-y-px"
                    >
                      {processing
                        ? t("scannerDetail.processing")
                        : t("scannerDetail.process_document")}
                    </LoadingButton>
                  </div>
                </div>
              </motion.div>

              {/* Right Column: WhatsApp Upload */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="bg-[#111b21] rounded-3xl shadow-lg border border-gray-800 p-8 h-full flex flex-col relative overflow-hidden text-white">
                  <div className="absolute top-0 right-0 p-32 bg-green-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

                  <div className="relative z-10 flex flex-col items-center text-center space-y-6 flex-1 justify-center">
                    <div className="bg-white p-3 rounded-2xl shadow-xl">
                      <QRCode
                        value={whatsappLink || "https://wa.me"}
                        size={150}
                        level="H"
                      />
                    </div>

                    <div className="space-y-3">
                      <p className="text-gray-300 text-sm font-medium">
                        {t("scannerDetail.scan_or_send")}
                      </p>
                      {/* <div className="text-2xl font-bold text-white tracking-widest bg-white/10 px-4 py-2 rounded-xl">
                        {saasNumber}
                      </div> */}
                      <p className="text-xs text-gray-400 max-w-[200px] mx-auto leading-relaxed">
                        {t("scannerDetail.automatically_processed_by", {
                          scannerName: scanner.name,
                        })}
                      </p>
                    </div>

                    {/* Sin número autorizado, un mensaje de WhatsApp no se
                        procesaría: WhatsApp es opcional y se activa en Ajustes.
                        Las cuentas nuevas ya no lo piden al registrarse. */}
                    {(user?.phones || []).length === 0 ? (
                      <div className="w-full rounded-xl bg-white/10 p-4 text-left">
                        <p className="text-sm font-semibold text-white">
                          {t("scannerDetail.whatsapp_optional_title")}
                        </p>
                        <p className="text-xs text-gray-300 mt-1 leading-relaxed">
                          {t("scannerDetail.whatsapp_optional_text")}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate("/settings")}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg text-sm font-bold transition-all"
                        >
                          <FaWhatsapp className="w-4 h-4" />
                          {t("scannerDetail.whatsapp_optional_cta")}
                        </button>
                      </div>
                    ) : (
                    <a
                      href={whatsappLink || "https://wa.me"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl font-bold transition-all w-full justify-center shadow-lg shadow-green-500/20"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                      {t("scannerDetail.open_whatsapp")}
                    </a>
                    )}

                    {/* WhatsApp no es el único canal: Telegram y el correo se
                        conectan en Ajustes y usan la misma plantilla. */}
                    <button
                      type="button"
                      onClick={() => navigate("/settings")}
                      data-testid="other-channels"
                      className="inline-flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <Mail className="w-3.5 h-3.5" />
                      {t("scannerDetail.other_channels")}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Results Section */}
            <AnimatePresence>
              {results && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary-500" />
                        {t("scannerDetail.results_title")}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {results.filename} •{" "}
                        {results.processingTime || "Just now"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={resetForm}
                        className="px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 rounded-lg transition-colors"
                      >
                        {t("scannerDetail.clear_results")}
                      </button>
                      <button
                        onClick={downloadResults}
                        className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-black rounded-lg transition-all shadow-md hover:shadow-lg"
                      >
                        {t("scannerDetail.download_json")}
                      </button>
                    </div>
                  </div>

                  <div className="p-8 space-y-8">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100 text-center">
                        <div className="text-3xl font-extrabold text-blue-600 mb-1">
                          {results.summary?.fields_with_values || 0}
                        </div>
                        <div className="text-xs font-bold text-blue-400 uppercase tracking-wider">
                          {t("scannerDetail.found_fields")}
                        </div>
                      </div>
                      <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-200 text-center">
                        <div className="text-3xl font-extrabold text-gray-600 mb-1">
                          {results.summary?.fields_null || 0}
                        </div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                          {t("scannerDetail.missing")}
                        </div>
                      </div>
                      <div className="bg-green-50/50 p-6 rounded-2xl border border-green-100 text-center">
                        <div className="text-3xl font-extrabold text-green-600 mb-1">
                          {results.tokensRemaining}
                        </div>
                        <div className="text-xs font-bold text-green-400 uppercase tracking-wider">
                          {t("scannerDetail.tokens_left")}
                        </div>
                      </div>
                    </div>

                    {/* Fields List */}
                    <div className="space-y-3">
                      {results.status === "queued" ? (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                          <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                            <Clock className="w-8 h-8" />
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">
                            {t("scannerDetail.document_queued_title")}
                          </h4>
                          <p className="text-gray-500 max-w-xs mx-auto mb-6">
                            {t("scannerDetail.queued_message")}
                          </p>
                          <button
                            onClick={() => fetchHistory()}
                            className="px-6 py-2 bg-white border border-gray-200 shadow-sm rounded-lg font-bold text-primary-600 hover:text-primary-700 hover:border-primary-200 transition-all"
                          >
                            {t("uiFixes.refresh_history")}
                          </button>
                        </div>
                      ) : results.extractedFields &&
                        results.extractedFields.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                          {results.extractedFields.map((field, index) => (
                            <div
                              key={index}
                              className="flex items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-100 transition-colors"
                            >
                              <div className="w-1/3 min-w-[150px] pr-4 border-r border-gray-100">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    {field.label || field.name}
                                  </span>
                                  {field.confidence < 0.8 && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-700">
                                      {Math.round(
                                        (field.confidence || 0) * 100
                                      )}
                                      %
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 pl-4">
                                <div className="text-sm font-medium text-gray-900 break-words font-mono">
                                  {field.value !== null &&
                                  field.value !== undefined ? (
                                    String(field.value)
                                  ) : (
                                    <span className="text-gray-400 italic">
                                      N/A
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-400 font-medium">
                          {t("uiFixes.no_fields")}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* History Section */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4 bg-gray-50/80 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100">
                    <Activity className="w-5 h-5 text-gray-500" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900">
                    {t("scannerDetail.history_title")}
                  </h3>
                </div>

                <div className="flex items-center gap-4">
                  {/* Filtro por origen: solo los orígenes presentes, con recuento. */}
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="source-filter">
                    {[
                      ["all", sourceCounts ? sourceCounts.all : history.length],
                      ...SOURCE_ORDER.map((k) => [
                        k,
                        sourceCounts ? sourceCounts[k] || 0 : history.filter((e) => sourceKeyOf(e) === k).length,
                      ]).filter(([k, n]) => n > 0 || sourceFilter === k),
                    ].map(([k, n]) => {
                      const active = sourceFilter === k;
                      const meta = SOURCE_META[k];
                      const Icon = meta?.icon || FiFilter;
                      return (
                        <button
                          key={k}
                          type="button"
                          data-filter={k}
                          onClick={() => {
                            if (k === sourceFilter) return;
                            sourceFilterRef.current = k;
                            setSourceFilter(k);
                            setSelectedEntries([]);
                            fetchHistory();
                          }}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
                            active ? "border-primary-500 bg-primary-600 text-white" : meta ? meta.cls : "border-gray-200 bg-white text-gray-700"
                          }`}
                        >
                          <Icon className="h-3 w-3" />
                          {k === "all" ? t("dashboard.source_all", "All sources") : t(meta.key, meta.fb)}
                          <span className="opacity-70">{n}</span>
                        </button>
                      );
                    })}
                  </div>
                  {isInvoiceScanner && (
                    <button
                      type="button"
                      data-track="month_export_open"
                      onClick={() => setShowMonthExport(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50"
                    >
                      <FiFileText className="h-3.5 w-3.5" />
                      {t("monthExport.open")}
                    </button>
                  )}
                  <button
                    onClick={() => fetchHistory()}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-100 bg-white">
                {historyLoading && history.length === 0 ? (
                  <div className="p-8">
                    <LoadingSkeleton variant="list" count={4} />
                  </div>
                ) : (
                  (() => {
                    // Normalize phone numbers for comparison (remove + prefix)
                    const normalizePhone = (phone) => {
                      if (!phone) return "";
                      return String(phone).replace(/^\+/, "");
                    };

                    const filteredHistory = history.filter((entry) => {
                      if (sourceFilter === "all") return true;
                      if (SOURCE_META[sourceFilter]) return sourceKeyOf(entry) === sourceFilter;
                      // Filtro antiguo por número de teléfono (si quedara guardado)
                      return normalizePhone(entry.sourceNumber) === normalizePhone(sourceFilter);
                    });

                    if (filteredHistory.length === 0 && !historyError) {
                      return (
                        <div className="py-24 text-center">
                          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Layers className="w-8 h-8 text-gray-300" />
                          </div>
                          <p className="text-gray-500 font-medium">
                            {history.length === 0
                              ? t("scannerDetail.no_history")
                              : t("scannerDetail.no_history_filtered") ||
                                "No results for this filter"}
                          </p>
                        </div>
                      );
                    }

                    const selectableCount =
                      filteredHistory.filter(isEntrySelectable).length;

                    return (
                      <motion.div
                        initial="hidden"
                        animate="show"
                        variants={{
                          show: { transition: { staggerChildren: 0.05 } },
                        }}
                      >
                        {/* Selection bar */}
                        {selectableCount > 0 && (
                          <div className="p-4 bg-gradient-to-r from-primary-50 to-purple-50 border-b border-primary-100 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              {selectedEntries.length > 0 ? (
                                <span className="text-sm font-semibold text-primary-700">
                                  {t("scannerDetail.selected_count", {
                                    count: selectedEntries.length,
                                  })}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-500">
                                  {t("scannerDetail.select_to_group")}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {selectedEntries.length === 0 ? (
                                <button
                                  onClick={() =>
                                    selectAllSelectable(filteredHistory)
                                  }
                                  className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-all"
                                >
                                  {t("scannerDetail.select_all")}
                                </button>
                              ) : (
                                <button
                                  onClick={clearSelection}
                                  className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                                >
                                  {t("scannerDetail.select_none")}
                                </button>
                              )}
                              {/* Fichero de la policía italiana (Alloggiati Web) con los
                                  DNI y pasaportes seleccionados (28-9-2026). */}
                              {isGuestIdScanner && (
                                <button
                                  onClick={() => {
                                    track("guest_file_open", { kind: "alloggiati" });
                                    setShowAlloggiati(true);
                                  }}
                                  disabled={selectedEntries.length === 0}
                                  className={`px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${
                                    selectedEntries.length > 0
                                      ? "bg-white text-primary-700 border border-primary-300 hover:bg-primary-50"
                                      : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                  }`}
                                >
                                  🇮🇹 {t("alloggiati.button")}
                                </button>
                              )}
                              {/* Parte de viajeros de España (SES.Hospedajes), 30-9-2026. */}
                              {isGuestIdScanner && (
                                <button
                                  onClick={() => {
                                    track("guest_file_open", { kind: "ses" });
                                    setShowSes(true);
                                  }}
                                  disabled={selectedEntries.length === 0}
                                  className={`px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${
                                    selectedEntries.length > 0
                                      ? "bg-white text-primary-700 border border-primary-300 hover:bg-primary-50"
                                      : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                  }`}
                                >
                                  🇪🇸 {t("ses.button")}
                                </button>
                              )}
                              <button
                                onClick={() => setShowBulkDownloadModal(true)}
                                disabled={selectedEntries.length === 0}
                                className={`px-4 py-1.5 text-xs font-bold rounded-lg flex items-center gap-2 transition-all ${
                                  selectedEntries.length > 0
                                    ? "bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                }`}
                              >
                                <Download className="w-3.5 h-3.5" />
                                {t("scannerDetail.group_and_download")}
                              </button>
                            </div>
                          </div>
                        )}

                        {filteredHistory.map((entry) => {
                          const entryId = entry.resultId || entry.requestId;
                          const job =
                            (jobs && jobs.get && jobs.get(entry.resultId)) ||
                            null;
                          const liveEvt =
                            job && job.lastEvent ? job.lastEvent : null;
                          const displayEntry = {
                            ...entry,
                            status:
                              (liveEvt &&
                                (liveEvt.payload?.status ||
                                  liveEvt.eventType)) ||
                              entry.status ||
                              "done",
                            tokensUsed:
                              (liveEvt && liveEvt.payload?.tokensUsed) ||
                              entry.tokensUsed ||
                              0,
                            summary:
                              (liveEvt &&
                                liveEvt.payload?.extractedJson?.summary) ||
                              entry.summary,
                            fields:
                              (liveEvt &&
                                liveEvt.payload?.extractedJson?.fields) ||
                              entry.fields ||
                              [],
                            file_type:
                              (liveEvt && liveEvt.payload?.fileType) ||
                              entry.file_type ||
                              entry.extractedJson?.doc_metadata?.tipo_archivo ||
                              null,
                            sourceType:
                              (liveEvt && liveEvt.payload?.sourceType) ||
                              entry.sourceType ||
                              entry.source_type,
                          };
                          return (
                            <HistoryItem
                              key={entry.resultId || entry.requestId}
                              entry={displayEntry}
                              t={t}
                              onDownloadResults={downloadHistoryResults}
                              onCleanJsonForUser={cleanJsonForUser}
                              selectable={isEntrySelectable(displayEntry)}
                              selected={selectedEntries.some(
                                (e) =>
                                  (e.resultId || e.requestId) ===
                                  (displayEntry.resultId ||
                                    displayEntry.requestId)
                              )}
                              onToggleSelect={toggleSelectEntry}
                              autoExpand={Boolean(
                                focusedResultId &&
                                  String(entryId) === String(focusedResultId)
                              )}
                              highlight={Boolean(
                                focusedResultId &&
                                  String(entryId) === String(focusedResultId)
                              )}
                            />
                          );
                        })}

                        {historyCursor && (
                          <div className="p-6 text-center bg-gray-50">
                            <button
                              onClick={() =>
                                fetchHistory({
                                  cursor: historyCursor,
                                  append: true,
                                })
                              }
                              disabled={historyLoading}
                              className="bg-white border border-gray-200 text-gray-600 px-6 py-2 rounded-full font-bold text-sm hover:bg-gray-50 shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                            >
                              {historyLoading
                                ? "Loading..."
                                : t("scannerDetail.load_more")}
                            </button>
                          </div>
                        )}
                      </motion.div>
                    );
                  })()
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <LoadingSkeleton variant="list" />
          </div>
        )}
      </motion.main>

      {/* Bulk Download Modal */}
      <AnimatePresence>
        {showMonthExport && (
          <MonthExportModal
            key="month-export"
            scannerId={id}
            scannerName={scanner?.name}
            scannerFields={scanner?.fields || []}
            roles={invoiceRoles}
            onClose={() => setShowMonthExport(false)}
          />
        )}
        {showSes && (
          <SesModal
            key="ses"
            entries={selectedEntries.filter((e) => (e.status || "done") === "done")}
            onClose={() => setShowSes(false)}
          />
        )}
        {showAlloggiati && (
        <AlloggiatiModal
          key="alloggiati"
          entries={selectedEntries.filter((e) => (e.status || "done") === "done")}
          onClose={() => setShowAlloggiati(false)}
        />
      )}
      {showBulkDownloadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBulkDownloadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary-100 rounded-xl text-primary-600">
                  <Download className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  {t("scannerDetail.group_and_download")}
                </h3>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                {t("scannerDetail.bulk_download_description")}
              </p>

              <div className="space-y-3 mb-6">
                <label
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    bulkFormat === "json"
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="bulkFormat"
                    value="json"
                    checked={bulkFormat === "json"}
                    onChange={(e) => setBulkFormat(e.target.value)}
                    className="sr-only"
                  />
                  <FileJson
                    className={`w-5 h-5 ${
                      bulkFormat === "json"
                        ? "text-primary-600"
                        : "text-gray-400"
                    }`}
                  />
                  <div>
                    <div className="font-semibold text-gray-900">JSON</div>
                    <div className="text-xs text-gray-500">
                      {t("scannerDetail.bulk_format_json")}
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    bulkFormat === "csv"
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="bulkFormat"
                    value="csv"
                    checked={bulkFormat === "csv"}
                    onChange={(e) => setBulkFormat(e.target.value)}
                    className="sr-only"
                  />
                  <FileSpreadsheet
                    className={`w-5 h-5 ${
                      bulkFormat === "csv"
                        ? "text-primary-600"
                        : "text-gray-400"
                    }`}
                  />
                  <div>
                    <div className="font-semibold text-gray-900">CSV</div>
                    <div className="text-xs text-gray-500">
                      {t("scannerDetail.bulk_format_csv")}
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    bulkFormat === "excel"
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="bulkFormat"
                    value="excel"
                    checked={bulkFormat === "excel"}
                    onChange={(e) => setBulkFormat(e.target.value)}
                    className="sr-only"
                  />
                  <FileSpreadsheet
                    className={`w-5 h-5 ${
                      bulkFormat === "excel"
                        ? "text-primary-600"
                        : "text-gray-400"
                    }`}
                  />
                  <div>
                    <div className="font-semibold text-gray-900">Excel</div>
                    <div className="text-xs text-gray-500">
                      {t("scannerDetail.bulk_format_excel")}
                    </div>
                  </div>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkDownloadModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  {t("common.cancel") || "Cancel"}
                </button>
                <button
                  onClick={handleConfirmBulkDownload}
                  className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-all flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t("scannerDetail.confirm_bulk_download")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
